import i18n from '@/i18n';
import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

/**
 * Випадний список на дизайн-токенах.
 *
 * Навіщо не native <select>: список опцій у нативного селекта малює браузер/ОС,
 * а не сторінка. Chromium ігнорує option:hover, підсвітку обраного малює сам, і
 * без colorScheme попап у темній темі виходить світлим — саме через це опції
 * читались погано. Керувати контрастом, hover, вибраним і disabled-станом можна
 * лише власним списком.
 *
 * Патерн скопійовано з наявної випадайки станції в Topbar (абсолютний попап,
 * hover-підсвітка, галочка на обраному) — нової механіки не вигадуємо, лише
 * переводимо на токени й Lucide.
 *
 * Клавіатура: ↓/↑ — рух, Enter — вибір, Esc — закрити, Home/End — краї.
 * Disabled-опції пропускаються при русі, але лишаються видимими й читабельними.
 */
export interface TokenSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface Props {
  value: string;
  options: TokenSelectOption[];
  onChange: (v: string) => void;
  /** Текст, коли нічого не обрано (value === ''). */
  placeholder?: string;
  disabled?: boolean;
  /** Ширина тригера; список ніколи не вужчий за тригер. */
  width?: number | string;
  maxWidth?: number | string;
  /** Над драверами (z 60/61) потрібен більший шар. */
  zIndex?: number;
  ariaLabel?: string;
  /** Підлаштування тригера під сусідні контроли (напр. фон/тінь топбара). */
  triggerStyle?: React.CSSProperties;
}

export default function TokenSelect({
  value, options, onChange, placeholder = i18n.t('ak_misc_select'),
  disabled = false, width, maxWidth, zIndex = 70, ariaLabel, triggerStyle,
}: Props) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState<number>(-1);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const selectedIdx = options.findIndex((o) => o.value === value);
  const selected = selectedIdx >= 0 ? options[selectedIdx] : null;

  // Закриття кліком повз компонент.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Відкриваючись, підсвічуємо поточний вибір.
  useEffect(() => { if (open) setHi(selectedIdx >= 0 ? selectedIdx : firstEnabled()); }, [open]); // eslint-disable-line

  // Тримаємо підсвічений рядок у полі зору.
  useEffect(() => {
    if (!open || hi < 0 || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-idx="${hi}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [hi, open]);

  const firstEnabled = () => options.findIndex((o) => !o.disabled);
  const step = (from: number, dir: 1 | -1) => {
    let i = from;
    for (let n = 0; n < options.length; n++) {
      i += dir;
      if (i < 0) i = options.length - 1;
      if (i >= options.length) i = 0;
      if (!options[i]?.disabled) return i;
    }
    return from;
  };

  const pick = (i: number) => {
    const o = options[i];
    if (!o || o.disabled) return;
    onChange(o.value);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault(); setOpen(true); return;
    }
    if (!open) return;
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => step(h, 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => step(h, -1)); }
    else if (e.key === 'Home') { e.preventDefault(); setHi(firstEnabled()); }
    else if (e.key === 'End') { e.preventDefault(); setHi(step(0, -1)); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(hi); }
    else if (e.key === 'Tab') setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative shrink-0" style={{ width, maxWidth }}>
      <button type="button" disabled={disabled} onKeyDown={onKeyDown}
        onClick={() => !disabled && setOpen((v) => !v)}
        aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel}
        className="w-full inline-flex items-center gap-2 text-left outline-none"
        style={{
          height: 36, borderRadius: 10, padding: '0 10px', fontSize: 12.5,
          background: 'var(--surface-2)', border: '1px solid var(--line)',
          color: selected ? 'var(--text)' : 'var(--text-muted)',
          cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1,
          ...triggerStyle,
        }}>
        <span className="truncate flex-1">{selected ? selected.label : placeholder}</span>
        <ChevronDown size={15} strokeWidth={2}
          style={{ color: 'var(--text-muted)', flexShrink: 0,
                   transform: open ? 'rotate(180deg)' : undefined, transition: 'transform .15s' }} />
      </button>

      {open && (
        <div ref={listRef} role="listbox" className="absolute left-0 top-full mt-1 py-1 overflow-y-auto"
          style={{
            minWidth: '100%', maxHeight: 280, zIndex,
            // Непрозора поверхня + контур + тінь: список чітко відділений від
            // сторінки, а не «плаває» напівпрозорим над контентом.
            background: 'var(--surface-solid)',
            border: '1px solid var(--line-strong)',
            borderRadius: 10, boxShadow: 'var(--shadow)',
            fontFamily: 'Urbanist, Inter, sans-serif',
          }}>
          {options.length === 0 && (
            <div className="px-3 py-2.5 text-[12.5px]" style={{ color: 'var(--text-muted)' }}>{i18n.t('ak_misc_empty')}</div>
          )}
          {options.map((o, i) => {
            const isSel = o.value === value;
            const isHi = i === hi && !o.disabled;
            return (
              <div key={`${o.value}-${i}`} data-idx={i} role="option"
                aria-selected={isSel} aria-disabled={o.disabled || undefined}
                onMouseEnter={() => !o.disabled && setHi(i)}
                onClick={() => pick(i)}
                className="flex items-center gap-2 px-3 py-2 text-[12.5px]"
                style={{
                  cursor: o.disabled ? 'not-allowed' : 'pointer',
                  // Обране — акцентна смуга + колір + галочка: видно навіть без наведення.
                  borderLeft: `3px solid ${isSel ? 'var(--series-3)' : 'transparent'}`,
                  // Наведення/клавіатура — помітна заливка, не ледь помітний відтінок.
                  background: isHi ? 'var(--surface-2)' : 'transparent',
                  // Disabled лишається читабельним: приглушений колір замість прозорості.
                  color: o.disabled ? 'var(--text-dim)'
                    : isSel ? 'var(--series-3)' : 'var(--text)',
                  fontWeight: isSel ? 600 : 400,
                }}>
                <span className="truncate flex-1">{o.label}</span>
                {isSel && <Check size={14} strokeWidth={2.4} style={{ color: 'var(--series-3)', flexShrink: 0 }} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
